import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PrintableReport from "./PrintableReport";
import PrintIcon from "@mui/icons-material/Print";

import { getSubjects } from "../../api/sponteAPI";
import {
  getGradesByStudent,
  getFinalResults,
  saveFinalResults,
  formatNotaParaExibicao
} from "../../api/autenticoAPI";
import {
  modalStyle,
  tableStyles,
  thStyles,
  tdStyles,
  Legends,
  Student,
  tableContainerStyles,
} from "./styles";
import {
  getGradeForSubject,
  calculateFinalAverage,
  calculateMediaFinalAfterExam,
  determineStatus,
  validateGrade,
  calcularNotaNecessaria,
} from "./utils";
import { AuthContext } from "../../contexts/Auth/AuthContext";

const ModalResultado = ({ open, setOpen, aluno, TurmaID, exportarPDF = false, onPDFExportComplete }) => {
  const [subjects, setSubjects] = useState([]);
  const [showPrintable, setShowPrintable] = useState(false);
  const [formattedGrades, setFormattedGrades] = useState({
    period1: [],
    period2: [],
    period3: [],
    period4: [],
  });
  const [finalExams, setFinalExams] = useState({});
  const [studentStatus, setStudentStatus] = useState({});
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState({
    exams: {},
    status: {},
    visibility: {},
  });
  const auth = useContext(AuthContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const isLoadingRef = useRef(false); // Proteção contra chamadas simultâneas


  const loadData = useCallback(async () => {
    // Evitar chamadas simultâneas
    if (isLoadingRef.current) {
      return;
    }

    try {
      if (!open && !exportarPDF) return;

      // Validar IDs antes de fazer as requisições
      if (!aluno?.AlunoID || aluno.AlunoID === null || aluno.AlunoID === undefined || aluno.AlunoID === '') {
        return;
      }
      if (!TurmaID || TurmaID === null || TurmaID === undefined || TurmaID === '') {
        return;
      }

      isLoadingRef.current = true;
      setLoading(true);

      // Usar Promise.allSettled para não falhar completamente se uma requisição falhar
      const [subjectsResult, gradesResult, finalResultsResult] = await Promise.allSettled([
        getSubjects(TurmaID),
        getGradesByStudent(aluno.AlunoID, TurmaID),
        getFinalResults(aluno.AlunoID, TurmaID),
      ]);

      // Processar subjects
      const subjects = subjectsResult.status === 'fulfilled' 
        ? (Array.isArray(subjectsResult.value) ? subjectsResult.value : [])
        : [];
      setSubjects(subjects);

      // Processar grades
      const grades = gradesResult.status === 'fulfilled' ? gradesResult.value : null;

      const formatted = {
        period1: [],
        period2: [],
        period3: [],
        period4: [],
      };

      if (grades) {
        ["period1", "period2", "period3", "period4"].forEach((period) => {
          if (grades[period]) {
            formatted[period] = grades[period].map((grade) => {
              // Formatar todos os valores de notas para exibição consistente
              return {
                DisciplinaID: String(grade.DisciplinaID),
                mb: formatNotaParaExibicao(grade.mb),
                at1: formatNotaParaExibicao(grade.at1),
                at2: formatNotaParaExibicao(grade.at2),
                at3: formatNotaParaExibicao(grade.at3),
                at4: formatNotaParaExibicao(grade.at4),
                at5: formatNotaParaExibicao(grade.at5),
                at6: formatNotaParaExibicao(grade.at6),
                ac1: formatNotaParaExibicao(grade.ac1),
                ac2: formatNotaParaExibicao(grade.ac2),
                rm: formatNotaParaExibicao(grade.rm),
                rb: formatNotaParaExibicao(grade.rb),
                used_recovery: grade.used_recovery || false,
                recovery_source: grade.recovery_source || "",
              };
            });
          }
        });
      }

      setFormattedGrades(formatted);

      // Processar finalResults
      const finalResults = finalResultsResult.status === 'fulfilled' ? finalResultsResult.value : null;

      if (finalResults?.disciplinas) {
        const exams = {};
        const status = {};
        const visibilityState = {};

        finalResults.disciplinas.forEach((discipline) => {
          const disciplinaId = String(discipline.DisciplinaID);
          // Lógica original que funcionava: só seta se tiver valor válido
          if (discipline.exameFinal && discipline.exameFinal !== "0") {
            exams[disciplinaId] = String(discipline.exameFinal);
          }
          status[disciplinaId] = discipline.situacao || "CURSANDO";
          visibilityState[disciplinaId] = discipline.visibilidade || false;
        });

        setFinalExams(exams);
        setStudentStatus(status);
        setVisibility(visibilityState);
        setInitialData({ exams, status, visibility: visibilityState });
        setHasChanges(false);
      }
      
      // Se for exportação automática de PDF, exibir a versão para impressão
      if (exportarPDF) {
        setShowPrintable(true);
      }

    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setSnackbar({
        open: true,
        message: "Erro ao carregar dados. Por favor, tente novamente.",
        severity: "error",
      });
      
      if (exportarPDF && onPDFExportComplete) {
        onPDFExportComplete(false);
      }
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [open, aluno?.AlunoID, TurmaID, exportarPDF, onPDFExportComplete]);

  const handleFinalExamChange = (disciplinaId, value) => {
    setFinalExams((prev) => {
      const newExams = { ...prev };
      if (!value || value === "0") {
        newExams[disciplinaId] = "";
      } else {
        newExams[disciplinaId] = value;
      }

      const mediaAnual = calculateFinalAverage(disciplinaId, formattedGrades);
      const newStatus = determineStatus(mediaAnual, value);
      setStudentStatus((prevStatus) => ({
        ...prevStatus,
        [disciplinaId]: newStatus,
      }));

      checkForChanges(newExams, studentStatus, visibility);
      return newExams;
    });
  };

  const handleVisibilityToggle = (disciplinaId) => {
    setVisibility((prev) => {
      const newVisibility = {
        ...prev,
        [disciplinaId]: !prev[disciplinaId],
      };
      checkForChanges(finalExams, studentStatus, newVisibility);
      return newVisibility;
    });
  };

  const handleToggleAllVisibility = () => {
    setVisibility((prev) => {
      const allVisible = subjects.every(
        (subject) => prev[subject.DisciplinaID] === true
      );

      const newVisibility = {};
      subjects.forEach((subject) => {
        newVisibility[subject.DisciplinaID] = !allVisible;
      });

      checkForChanges(finalExams, studentStatus, newVisibility);
      return newVisibility;
    });
  };
  useEffect(() => {
    if (open || exportarPDF) {
      loadData();
    }
  }, [open, exportarPDF, aluno?.AlunoID, TurmaID]);

  useEffect(() => {
    if (!open && !exportarPDF) return;

    subjects.forEach((subject) => {
      const mediaAnual = calculateFinalAverage(
        subject.DisciplinaID,
        formattedGrades
      );
      const exameFinal = finalExams[subject.DisciplinaID];
      const newStatus = determineStatus(mediaAnual, exameFinal);

      setStudentStatus((prev) => ({
        ...prev,
        [subject.DisciplinaID]: newStatus,
      }));
    });
  }, [formattedGrades, finalExams, subjects, open, exportarPDF]);

  const handleStatusChange = (disciplinaId, value) => {
    setStudentStatus((prev) => {
      const newStatus = { ...prev, [disciplinaId]: value };
      checkForChanges(finalExams, newStatus, visibility);
      return newStatus;
    });
  };

  const checkForChanges = (currentExams, currentStatus, currentVisibility) => {
    const examsChanged =
      JSON.stringify(currentExams) !== JSON.stringify(initialData.exams);
    const statusChanged =
      JSON.stringify(currentStatus) !== JSON.stringify(initialData.status);
    const visibilityChanged =
      JSON.stringify(currentVisibility) !==
      JSON.stringify(initialData.visibility);
    setHasChanges(examsChanged || statusChanged || visibilityChanged);
  };

  const handleSaveResults = async () => {
    try {
      setLoading(true);

      const gradesData = subjects.map((subject) => {
        // Campo `mediaFinal` na API persiste a média anual (M.A.) em notas_situacao.media_final
        const mediaAnual = calculateFinalAverage(
          subject.DisciplinaID,
          formattedGrades
        );

        // Garantir que disciplinaId seja número inteiro (backend espera number)
        const disciplinaIdNum = typeof subject.DisciplinaID === 'string' 
          ? parseInt(subject.DisciplinaID, 10) 
          : Number(subject.DisciplinaID);

        return {
          disciplinaId: disciplinaIdNum,
          mediaFinal: validateGrade(mediaAnual),
          exameFinal: finalExams[subject.DisciplinaID] || "",
          situacao: studentStatus[subject.DisciplinaID] || "CURSANDO",
          visibilidade: visibility[subject.DisciplinaID] || false,
        };
      });

      const response = await saveFinalResults(
        aluno.AlunoID,
        TurmaID,
        gradesData
      );

      if (response?.success) {
        setSnackbar({
          open: true,
          message: "Resultados salvos com sucesso!",
          severity: "success",
        });
        // Recarregar dados do banco - sempre buscar dados atualizados
        await loadData();
      } else {
        throw new Error(response?.message || "Erro ao salvar resultados");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setSnackbar({
        open: true,
        message: `Erro ao salvar os resultados: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Não renderizar modal caso seja apenas exportação de PDF
  if (exportarPDF) {
    return (
      <PrintableReport
        open={showPrintable}
        onClose={() => {
          setShowPrintable(false);
          if (onPDFExportComplete) onPDFExportComplete(true);
        }}
        aluno={aluno}
        subjects={subjects}
        formattedGrades={formattedGrades}
        finalExams={finalExams}
        studentStatus={studentStatus}
        unidade={auth.user?.unit}
        exportarAutomaticamente={true}
      />
    );
  }

  return (
    <>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-resultado"
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.8)",
        }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
              }}
            >
              Boletim Geral
            </Typography>
            <Button
              variant="contained"
              onClick={handleSaveResults}
              disabled={loading || !hasChanges}
              sx={{
                bgcolor: "#2563eb",
                "&:hover": {
                  bgcolor: "#1d4ed8",
                },
                "&:disabled": {
                  bgcolor: "#94a3b8",
                },
              }}
            >
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => setShowPrintable(true)}
              sx={{
                marginLeft: 2,
              }}
            >
              Versão para Impressão
            </Button>
          </Box>

          <Box sx={Legends}>
            <Typography sx={Student}>{aluno.nome.split("-")[1]}</Typography>
          </Box>

          <Box sx={tableContainerStyles}>
            <table style={tableStyles}>
              <thead>
                <tr>
                  <th style={thStyles}>Disciplina</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>1º B</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>2º B</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>3º B</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>4º B</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>MA</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>PO</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>NE</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>EF</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>MF</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>Situação</th>
                  <th style={{ ...thStyles, textAlign: "center" }}>
                    Visibilidade
                    <IconButton
                      onClick={handleToggleAllVisibility}
                      size="small"
                      sx={{
                        padding: "4px",
                        marginLeft: "4px",
                        backgroundColor: "rgba(0,0,0,0.04)",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {subjects.every(
                        (subject) => visibility[subject.DisciplinaID] === true
                      ) ? (
                        <VisibilityIcon sx={{ fontSize: "1rem" }} />
                      ) : (
                        <VisibilityOffIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((subject, index) => {
                  const mediaAnual = calculateFinalAverage(
                    String(subject?.DisciplinaID),
                    formattedGrades
                  );
                  const mediaPosExame = calculateMediaFinalAfterExam(
                    mediaAnual,
                    finalExams[subject?.DisciplinaID]
                  );
                  
                  // Calcular PO (Pontos Obtidos) CORRETAMENTE: somar as 4 notas bimestrais
                  // Não multiplicar média por 4 (isso estava ERRADO!)
                  const bim1 = getGradeForSubject(subject?.DisciplinaID, 1, formattedGrades);
                  const bim2 = getGradeForSubject(subject?.DisciplinaID, 2, formattedGrades);
                  const bim3 = getGradeForSubject(subject?.DisciplinaID, 3, formattedGrades);
                  const bim4 = getGradeForSubject(subject?.DisciplinaID, 4, formattedGrades);
                  
                  let pontos = "-";
                  if (bim1 !== "-" && bim2 !== "-" && bim3 !== "-" && bim4 !== "-") {
                    const b1 = parseFloat(bim1.replace(",", ".").replace("*", ""));
                    const b2 = parseFloat(bim2.replace(",", ".").replace("*", ""));
                    const b3 = parseFloat(bim3.replace(",", ".").replace("*", ""));
                    const b4 = parseFloat(bim4.replace(",", ".").replace("*", ""));
                    
                    if (!isNaN(b1) && !isNaN(b2) && !isNaN(b3) && !isNaN(b4)) {
                      const soma = b1 + b2 + b3 + b4;
                      const somaArredondada = Math.round(soma * 10) / 10;
                      pontos = somaArredondada.toFixed(1);
                    }
                  }
                  
                  const maNum = parseFloat(mediaAnual.replace(",", ".").replace("*", ""));
                  const notaNecessaria = calcularNotaNecessaria(pontos, maNum);

                  return (
                    <tr
                      key={subject?.Nome || index}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb",
                      }}
                    >
                      <td style={{ ...tdStyles, fontWeight: 500 }}>
                        {subject?.Nome || "-"}
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        {getGradeForSubject(
                          subject?.DisciplinaID,
                          1,
                          formattedGrades
                        )}
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        {getGradeForSubject(
                          subject?.DisciplinaID,
                          2,
                          formattedGrades
                        )}
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        {getGradeForSubject(
                          subject?.DisciplinaID,
                          3,
                          formattedGrades
                        )}
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        {getGradeForSubject(
                          subject?.DisciplinaID,
                          4,
                          formattedGrades
                        )}
                      </td>
                      <td
                        style={{
                          ...tdStyles,
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {mediaAnual}
                      </td>
                      <td
                        style={{
                          ...tdStyles,
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {pontos}
                      </td>
                      <td
                        style={{
                          ...tdStyles,
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {notaNecessaria}
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        <TextField
                          size="small"
                          value={finalExams[subject?.DisciplinaID] || ""}
                          onChange={(e) =>
                            handleFinalExamChange(
                              subject?.DisciplinaID,
                              e.target.value
                            )
                          }
                          inputProps={{
                            style: {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                            },
                            placeholder: "-",
                          }}
                          sx={{
                            width: "80px",
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      </td>
                      <td
                        style={{
                          ...tdStyles,
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {mediaPosExame}
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        <Select
                          size="small"
                          value={
                            studentStatus[subject?.DisciplinaID] || "CURSANDO"
                          }
                          onChange={(e) =>
                            handleStatusChange(
                              subject?.DisciplinaID,
                              e.target.value
                            )
                          }
                          sx={{
                            width: "140px",
                            fontSize: "0.75rem",
                            height: "28px",
                            "& .MuiSelect-select": {
                              padding: "4px 8px",
                            },
                          }}
                        >
                          <MenuItem
                            sx={{ fontSize: "0.75rem" }}
                            value="CURSANDO"
                          >
                            CURSANDO
                          </MenuItem>
                          <MenuItem
                            sx={{ fontSize: "0.75rem" }}
                            value="APROVADO"
                          >
                            APROVADO
                          </MenuItem>
                          <MenuItem
                            sx={{ fontSize: "0.75rem" }}
                            value="REPROVADO"
                          >
                            REPROVADO
                          </MenuItem>
                          <MenuItem
                            sx={{ fontSize: "0.75rem" }}
                            value="EM EXAME"
                          >
                            EM EXAME
                          </MenuItem>
                        </Select>
                      </td>
                      <td style={{ ...tdStyles, textAlign: "center" }}>
                        <IconButton
                          onClick={() =>
                            handleVisibilityToggle(subject?.DisciplinaID)
                          }
                          size="small"
                          sx={{ padding: "4px" }}
                        >
                          {visibility[subject?.DisciplinaID] ? (
                            <VisibilityIcon sx={{ fontSize: "1.1rem" }} />
                          ) : (
                            <VisibilityOffIcon sx={{ fontSize: "1.1rem" }} />
                          )}
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
          
          {/* Legenda */}
          <Box sx={{ mt: 2, p: 1, fontSize: '0.75rem', color: 'text.secondary', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <span>MA - Média Anual</span>
            <span>MF - Média Final (após exame; sem EF, igual à MA)</span>
            <span>EF - Exame Final</span>
            <span>* - Nota após recuperação</span>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <PrintableReport
        open={showPrintable}
        onClose={() => setShowPrintable(false)}
        aluno={aluno}
        subjects={subjects}
        formattedGrades={formattedGrades}
        finalExams={finalExams}
        studentStatus={studentStatus}
        unidade={auth.user?.unit}
      />
    </>
  );
};

export default ModalResultado;
